import type { ProFeature, Subscription, SubscriptionPlan } from '../types'
import { FREE_TIER_LIMITS } from '../types'

/**
 * Check if user has access to a Pro feature
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const canUseFeature = (plan: SubscriptionPlan, _feature: ProFeature): boolean => {
  if (plan === 'pro') return true

  // Free tier doesn't have access to Pro features
  return false
}

/**
 * Check if user can add more applications
 * @param totalAppCount - total number of applications (including archived)
 */
export const canAddApplication = (plan: SubscriptionPlan, totalAppCount: number): boolean => {
  if (plan === 'pro') return true
  return totalAppCount < FREE_TIER_LIMITS.maxActiveApps
}

/**
 * Get remaining app slots for free tier
 * @param totalAppCount - total number of applications (including archived)
 */
export const getRemainingAppSlots = (plan: SubscriptionPlan, totalAppCount: number): number | null => {
  if (plan === 'pro') return null // unlimited
  return Math.max(0, FREE_TIER_LIMITS.maxActiveApps - totalAppCount)
}

/**
 * Check if subscription is active (paid and valid)
 */
export const isSubscriptionActive = (subscription: Subscription | null): boolean => {
  if (!subscription) return false
  return subscription.plan === 'pro' &&
    (subscription.status === 'active' || subscription.status === 'trialing')
}

/**
 * Get user's effective plan
 */
export const getEffectivePlan = (subscription: Subscription | null): SubscriptionPlan => {
  if (!subscription) return 'free'
  if (subscription.status === 'active' || subscription.status === 'trialing') {
    return subscription.plan
  }
  return 'free'
}

/**
 * Feature descriptions for upgrade prompts
 */
export const PRO_FEATURES = [
  {
    id: 'unlimited_apps' as ProFeature,
    title: 'Unlimited Applications',
    description: 'Track as many job applications as you need',
    icon: '📋',
  },
  {
    id: 'advanced_analytics' as ProFeature,
    title: 'Advanced Analytics',
    description: 'Sankey diagrams, compensation charts, and more',
    icon: '📊',
  },
  {
    id: 'multi_currency' as ProFeature,
    title: 'Multi-Currency Conversion',
    description: 'Convert salaries between 11 currencies',
    icon: '💱',
  },
  {
    id: 'salary_insights' as ProFeature,
    title: 'Salary Insights',
    description: 'Detailed compensation stats and market comparisons',
    icon: '💰',
  },
  {
    id: 'resume_analysis' as ProFeature,
    title: 'Resume Analysis',
    description: 'AI-powered CV feedback with ATS optimization tips',
    icon: '📄',
  },
  {
    id: 'resume_match' as ProFeature,
    title: 'Resume Match',
    description: 'Compare your CV to specific job postings and get tailored tips',
    icon: '🎯',
  },
  {
    id: 'resume_builder' as ProFeature,
    title: 'Resume Builder',
    description: 'Build resumes, tailor them to specific jobs with AI, and export to PDF',
    icon: '📝',
  },
  {
    id: 'practice_quizzes' as ProFeature,
    title: 'Interview Quizzes',
    description: 'AI-generated interview questions tailored to your applications',
    icon: '🎓',
  },
  {
    id: 'ai_chat' as ProFeature,
    title: 'AI Assistant',
    description: 'Your job search assistant',
    icon: '🐜',
  },
  {
    id: 'mock_interview' as ProFeature,
    title: 'Mock Interviews',
    description: 'AI-powered speech-to-speech interview practice (2/week)',
    icon: '🎙️',
  },
] as const

export { PRICING } from '@job-tracker/shared'
export type { BillingPeriod } from '@job-tracker/shared'
